import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { FileText, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export const ResetPasswordPage = ({ token, onComplete }) => {
  const { resetPassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Reset password submit - token:', token ? 'present' : 'MISSING');
    
    if (!token) {
      toast.error('Reset token is missing. Please use the link from your email.');
      return;
    }
    
    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      console.log('Calling resetPassword API...');
      await resetPassword(token, password);
      console.log('Reset successful!');
      setSuccess(true);
    } catch (err) {
      console.error('Reset error:', err);
      const message = err.response?.data?.detail || 'Reset failed. The link may have expired.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-[#2C4C3B]/10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-6 h-6 text-[#2C4C3B]" />
            </div>
            <h2 className="text-lg font-medium text-[#1C1C1C] mb-2">Password Reset Complete</h2>
            <p className="text-sm text-[#595959] mb-6">
              Your password has been reset successfully.
            </p>
            <Button
              onClick={onComplete}
              className="rounded-xl"
              data-testid="continue-to-login-btn"
            >
              Continue to Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E5E5E5] p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B] flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <h1 className="font-semibold text-xl text-[#1C1C1C]">Rubric Builder</h1>
          </div>

          <h2 className="text-lg font-medium text-center text-[#1C1C1C] mb-2">Reset your password</h2>
          <p className="text-sm text-[#595959] text-center mb-6">
            Enter your new password below
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <PasswordInput
                id="password"
                placeholder="At least 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="new-password-input"
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <PasswordInput
                id="confirmPassword"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-new-password-input"
                disabled={loading}
              />
            </div>

            <Button
              type="submit"
              className="w-full rounded-xl"
              disabled={loading}
              data-testid="reset-password-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};
